/**
 * AuditLogPage — admin audit-log viewer (STORY-008-008). Inline multi-field
 * filter toolbar (actor/action/entity_type/q text inputs + from/to date inputs
 * + reset — built here, NOT the P0 _shared FilterToolbar) + AdminDataTable +
 * row-activated AuditDetailDrawer (before/after JSON diff). "Load more" keyset
 * pagination. CSV export streams from the self-audited backend endpoint.
 * READ-only.
 *
 * IP is masked server-side (raw only for ops.admin_actions.read); the client
 * renders it verbatim. A 408 audit_log.query_timeout surfaces as a banner
 * prompting narrower filters (AC#11).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError, getApiClientConfig } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { AdminDataTable } from "@/features/admin/_shared/AdminDataTable";
import {
  EMPTY_AUDIT_FILTERS,
  auditExportCsvUrl,
  type AuditFilters,
  type AuditLogItem,
} from "@/features/admin/audit/api";
import { useAuditLog } from "@/features/admin/audit/hooks";
import { AuditDetailDrawer } from "@/features/admin/audit/AuditDetailDrawer";

/**
 * Inline filter-field config. The audit endpoint takes four INDEPENDENT text
 * filters (actor/action/entity_type/q) plus separate from/to date inputs — a
 * shape the P0 `_shared` FilterToolbar (search + chips + single date-range)
 * cannot express, so this surface renders its own toolbar. `key` is the
 * `AuditFilters` state field; `testid` is the suffix in `admin-audit-filter-*`
 * (note `entity_type` testid maps to the `entityType` state key); `i18n` is the
 * `audit.filter.*` label key.
 */
const AUDIT_FILTER_FIELDS = [
  { key: "actor", testid: "actor", i18n: "actor", type: "text" },
  { key: "action", testid: "action", i18n: "action", type: "text" },
  { key: "entityType", testid: "entity_type", i18n: "entity", type: "text" },
  { key: "q", testid: "q", i18n: "q", type: "text" },
  { key: "from", testid: "from", i18n: "from", type: "datetime-local" },
  { key: "to", testid: "to", i18n: "to", type: "datetime-local" },
] as const satisfies ReadonlyArray<{
  key: keyof AuditFilters;
  testid: string;
  i18n: string;
  type: "text" | "datetime-local";
}>;

/**
 * Debounce window for the free-text filters (actor/action/entity_type/q). The
 * audit query endpoint is timeout-guarded, so we coalesce keystrokes rather
 * than firing a query per character. Date pickers + reset stay immediate.
 */
export const AUDIT_TEXT_DEBOUNCE_MS = 350;

export function AuditLogPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  // `draft` drives the controlled inputs (updates on every keystroke). `filters`
  // feeds `useAuditLog`: text fields are debounced into it, while date pickers
  // and reset write it immediately (see handlers below).
  const [draft, setDraft] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Debounce the free-text fields into the query filters.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((f) => ({
        ...f,
        actor: draft.actor,
        action: draft.action,
        entityType: draft.entityType,
        q: draft.q,
      }));
    }, AUDIT_TEXT_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [draft.actor, draft.action, draft.entityType, draft.q]);

  const { data, error, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useAuditLog(filters);

  const rows: AuditLogItem[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total_estimate ?? 0;
  const isTimeout = isError && error instanceof ApiError && error.code === "audit_log.query_timeout";

  const columns = [
    { key: "occurred_at", label: t("audit.columns.time") },
    { key: "actor", label: t("audit.columns.actor") },
    { key: "action", label: t("audit.columns.action") },
    { key: "entity", label: t("audit.columns.entity") },
    { key: "ip", label: t("audit.columns.ip") },
  ] as const;

  const renderCell = (r: AuditLogItem, key: string): ReactElement | string => {
    switch (key) {
      case "occurred_at":
        return <span className="text-[var(--g-secondary)]">{new Date(r.occurred_at).toLocaleString()}</span>;
      case "actor":
        return r.actor?.email ?? r.actor?.display_name ?? "—";
      case "action":
        return <span className="font-medium">{r.action}</span>;
      case "entity":
        return (
          <span className="flex items-center gap-1">
            <GPill tone="neutral" size="sm">{r.entity_type}</GPill>
            {r.entity_id ? <span className="text-xs text-[var(--g-secondary)]">{r.entity_id.slice(0, 8)}</span> : null}
          </span>
        );
      case "ip":
        return <span className="text-[var(--g-secondary)]">{r.ip ?? "—"}</span>;
      default:
        return "";
    }
  };

  const renderMobileCard = (r: AuditLogItem): ReactElement => (
    <div className="flex flex-col gap-0.5">
      <div className="font-medium">{r.action}</div>
      <div className="text-sm text-[var(--g-secondary)]">
        {r.actor?.email ?? "—"} · {r.entity_type}
      </div>
      <div className="text-xs text-[var(--g-secondary)]">{new Date(r.occurred_at).toLocaleString()}</div>
    </div>
  );

  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const path = auditExportCsvUrl(filters);
      // The JWT lives in JS memory (never a cookie), so a bare credentialed
      // fetch would 401. Mirror the sibling OOC export (ooc/api.ts) and attach
      // the in-memory access token as a Bearer header.
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(getApiClientConfig().baseUrl + path, {
        method: "GET",
        credentials: "include",
        headers: token
          ? { Accept: "text/csv", Authorization: `Bearer ${token}` }
          : { Accept: "text/csv" },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = "audit-log.csv";
      a.click();
      URL.revokeObjectURL(href);
      toast.success(t("audit.export.success"));
    } catch {
      toast.error(t("error.generic"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-audit-page" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{t("audit.title")}</h1>
        <button
          type="button"
          data-testid="admin-audit-export"
          onClick={() => void handleExport()}
          disabled={exporting}
          className="min-h-[44px] rounded-full bg-[var(--g-elevated)] px-4 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
        >
          {exporting ? t("common.refreshing") : t("audit.export.button")}
        </button>
      </div>

      {/*
        Inline multi-field filter toolbar (NOT the P0 _shared FilterToolbar).
        The audit endpoint needs four independent text filters
        (actor/action/entity_type/q) + separate from/to date inputs, which the
        kit's search+chips+single-date-range contract cannot express. Each
        control is token-styled (--g-*) and emits `admin-audit-filter-<testid>`.
      */}
      <div
        data-testid="admin-audit-filters"
        role="search"
        aria-label={t("audit.filter.legend")}
        className="flex flex-wrap items-end gap-3 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-elevated)] p-3"
      >
        {AUDIT_FILTER_FIELDS.map((field) => (
          <label
            key={field.key}
            className="flex flex-col gap-1 text-xs font-medium text-[var(--g-secondary)]"
          >
            {t(`audit.filter.${field.i18n}`)}
            <input
              type={field.type}
              data-testid={`admin-audit-filter-${field.testid}`}
              value={draft[field.key] ?? ""}
              onChange={(e) => {
                const value = e.target.value === "" ? null : e.target.value;
                setDraft((d) => ({ ...d, [field.key]: value }));
                // Date pickers commit immediately; text fields debounce (effect).
                if (field.type === "datetime-local") {
                  setFilters((f) => ({ ...f, [field.key]: value }));
                }
              }}
              className="min-h-[44px] rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm text-[var(--g-primary-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
            />
          </label>
        ))}
        <button
          type="button"
          data-testid="admin-audit-filter-reset"
          onClick={() => {
            setDraft(EMPTY_AUDIT_FILTERS);
            setFilters(EMPTY_AUDIT_FILTERS);
          }}
          className="min-h-[44px] rounded-full bg-[var(--g-elevated)] px-4 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
        >
          {t("audit.filter.reset")}
        </button>
      </div>

      <p className="text-sm text-[var(--g-secondary)]" data-testid="admin-audit-total">
        {t("audit.totalEstimate", { count: total })}
      </p>

      {isTimeout ? (
        <div
          data-testid="admin-audit-timeout"
          role="alert"
          className="rounded-[var(--g-radius-sm)] border border-[var(--g-danger)] bg-[var(--g-elevated)] p-3 text-sm text-[var(--g-danger)]"
        >
          {t("audit.timeout")}
        </div>
      ) : (
        <AdminDataTable<AuditLogItem>
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          renderCell={renderCell}
          renderMobileCard={renderMobileCard}
          onRowActivate={(r) => setSelectedId(r.id)}
          rowAriaLabel={(r) => `${r.action} ${r.entity_type}`}
          testid="admin-audit"
          isLoading={isLoading}
          isError={isError}
          emptyLabel={t("audit.empty")}
          errorLabel={t("error.loadFailed")}
        />
      )}

      {hasNextPage && !isTimeout ? (
        <button
          type="button"
          data-testid="admin-audit-load-more"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mx-auto min-h-[44px] rounded-full bg-[var(--g-elevated)] px-6 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
        >
          {isFetchingNextPage ? t("common.refreshing") : t("common.loadMore")}
        </button>
      ) : null}

      <AuditDetailDrawer auditId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

export default AuditLogPage;
